/* @flow */
import * as React from 'react';
import { graphql } from 'react-apollo';
import idx from 'idx';
import invariant from 'invariant';
import cx from 'classnames';

import Chat from 'entities/Chat';
import ContactabilityBadge from 'components/ContactabilityBadge';
import contactabilityEducationQuery from 'chat/queries/contactabilityEducationQuery.gql';
import Dialog from 'components/Dialog';
import Feature from 'components/Feature';
import HumanArray from 'components/HumanArray';
import Link from 'components/Link';
import ReachabilityBadge from 'components/ReachabilityBadge';
import Text from 'components/Text';
import TextWithGlyph from 'components/TextWithGlyph';

import type { ReachabilityInfo } from 'components/ReachabilityBadge';
import type { ContactabilityStatus } from 'queries/__generated__/globalGQLTypes';

import './styles/ConversationHeader.scss';

type Props = {
  chat?: Chat,
  title?: string,
  subtitle?: string,
  onTitleClicked?: Function,
  onRequestHide?: Function,
  children?: React.Element<any>,
  otherUserUuid?: string,
  otherUser: {
    id: number,
    uuid: string,
    name: string,
    contactabilityStatus: ContactabilityStatus,
    canImproveContactability: boolean,
    devices: {
      type: string,
      enabled: boolean,
    }[],
    reachabilityInfo: ReachabilityInfo,
  },
};

const mobileDeviceDisabled = device =>
  device.enabled === false && (device.type === 'apns' || device.type === 'sms');

class ConversationHeader extends React.Component<Props> {
  shouldHideToastsFn() {
    return !Dialog.getCurrent();
  }

  renderParticipantNames() {
    const { chat } = this.props;
    invariant(chat, 'renderParticipantNames called with missing chat');
    const names = chat.getDisplayNames();
    return (
      <Text size="large" truncate>
        <HumanArray sample={names} total={names.length} />
      </Text>
    );
  }

  renderContactabilityBanner() {
    const { otherUser } = this.props;
    if (!otherUser) {
      return null;
    }

    const { name, devices, canImproveContactability } = otherUser;

    if (!canImproveContactability) {
      return null;
    }
    const hasDisabledDevice = devices.some(mobileDeviceDisabled);

    if (hasDisabledDevice) {
      return (
        <div className="ConversationHeader-banner" data-test="contactability-banner">
          <Text size="xsmall" align="center">
            {name} needs to update their notification settings to get messages on their phone.
          </Text>
        </div>
      );
    }

    return null;
  }

  renderReachabilityBanner() {
    const { otherUser } = this.props;

    if (!otherUser || !otherUser.reachabilityInfo) {
      return null;
    }

    const { reachabilityInfo } = otherUser;
    const { reasonLong, status, action } = reachabilityInfo;

    if (!reasonLong) {
      return null;
    }

    const mapActionToLinkComponent = (link: ?string, text: string) => ({
      link: link ? (
        <Link to={link} target="_blank">
          {text}
        </Link>
      ) : null,
      add_number: null,
    });

    const linkComponent = action
      ? mapActionToLinkComponent(action.link, action.text)[action.type]
      : null;

    const component = {
      unreachable: (
        <div className="ConversationHeader-banner-content">
          <TextWithGlyph glyph="explanation-circle" glyphColor="apple" size="xsmall" truncate>
            {reasonLong} {linkComponent}
          </TextWithGlyph>
        </div>
      ),
      poor: (
        <Text size="xsmall" align="center" truncate>
          {reasonLong} {linkComponent}
        </Text>
      ),
      rich: null,
    };

    return (
      <div className="ConversationHeader-banner" data-test="reachability-banner">
        {component[status]}
      </div>
    );
  }

  render() {
    const { chat, title, subtitle, otherUser } = this.props;
    const partnerLeft = chat && chat.isPresent() && !!chat.getLeftMemberships()[0];
    let participantNames = null;
    const titleTextSize = subtitle ? 'medium' : 'large';

    if (chat && !title) {
      participantNames = (
        <Text size={titleTextSize} truncate>
          {chat.hasTitle() ? chat.getTitle() : this.renderParticipantNames()}
          {otherUser && chat.isIndividualType() ? (
            <Feature
              name="2018.oct.web.reachability_status"
              on={<ReachabilityBadge reachability={otherUser.reachabilityInfo} />}
              off={<ContactabilityBadge status={otherUser.contactabilityStatus} />}
            />
          ) : null}
        </Text>
      );
    }
    const titleText = (
      <Text size={titleTextSize} truncate>
        {title}
      </Text>
    );

    let linkedTitle = title ? titleText : participantNames;
    if (this.props.onTitleClicked) {
      linkedTitle = (
        <Link color="chalkboard" onClick={this.props.onTitleClicked}>
          {linkedTitle}
        </Link>
      );
    }

    return (
      <div>
        <div
          className={cx({
            ConversationHeader: true,
            'ConversationHeader-disabled': (chat && chat.isUnsubscribed()) || partnerLeft,
          })}>
          <div className="ConversationHeader-titles">
            <h1
              className={cx('ConversationHeader-title', {
                'ConversationHeader-title-shiftUp': !!subtitle,
              })}>
              {linkedTitle}
            </h1>
            <Text className="ConversationHeader-subtitle" size="xsmall" truncate>
              {subtitle}
            </Text>
          </div>
          <div className="ConversationHeader-buttons">{this.props.children}</div>
        </div>
        {chat && chat.isIndividualType() && (
          <Feature
            name="2018.oct.web.reachability_status"
            on={this.renderReachabilityBanner()}
            off={this.renderContactabilityBanner()}
          />
        )}
      </div>
    );
  }
}
const withData = graphql(contactabilityEducationQuery, {
  options: ({ otherUserUuid }) => ({
    variables: { uuid: otherUserUuid },
  }),
  props: ({ data }: { data: contactabilityEducationQuery }) => ({
    otherUser: idx(data, _ => _.me.connectedUsers[0]),
  }),
  skip: ({ otherUserUuid }) => !otherUserUuid,
});

export { ConversationHeader };
export default withData(ConversationHeader);
